import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormHelperText, Grid, MenuItem, Select, SelectProps, SelectChangeEvent, Typography, Box, Paper, IconButton } from '@mui/material';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { CloudUpload } from '@mui/icons-material';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import { FieldAttributes, Form, Formik, useField } from 'formik';
import Swal from 'sweetalert2';
import useAuth from '../../context/AuthProvider';
import { apiClient } from '../../helper/api';
import { getRowsPerPageOptions } from './CustomFunctions';

const ALLOWED_FILE_TYPES = ['.pdf', '.doc', '.docx', '.xls', '.xlsx'] as const;
const FILE_ACCEPT_ATTRIBUTE = '.pdf,.doc,.docx,.xls,.xlsx';

interface Iprops {
    handleClose: () => void;
    Open: boolean;
    onSuccess?: () => void;
    onLoadingChange?: (loading: boolean) => void;
}

interface Partner {
    partnersId: number;
    customerName: string;
}

interface UploadedFile {
    id: string;
    file: File;
    name: string;
    size: number;
}

const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'] as const;
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

const createFileObject = (file: File): UploadedFile => ({
    id: Math.random().toString(36).substr(2, 9),
    file,
    name: file.name,
    size: file.size,
});

export const AddStampedDocument: React.FC<Iprops> = ({ handleClose, Open, onSuccess, onLoadingChange }) => {
    const { user } = useAuth();
    const token = user?.token;
    const [loading, setLoadingState] = useState(false);
    const setLoading = (value: boolean) => {
        setLoadingState(value);
        onLoadingChange?.(value);
    };
    const [formData, setFormData] = useState({
        partnersId: 0,
        documentType: '',
    });
    const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
    const [isDragOver, setIsDragOver] = useState(false);
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(0);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const initialPartnerData: Partner = { partnersId: 0, customerName: "" };
    const [partners, setPartners] = useState<Partner[]>([initialPartnerData]);
    
    const fetchPartnersData = () => {
        apiClient('/Partners/getPartners', {
            method: "GET",
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            })
        }).then((responseJson) => {
            if (!responseJson) return;
            setPartners(responseJson.data);
        }).catch(error => {
            console.error("Error fetching partners:", error)
        });
    };

    useEffect(() => {
        if (Open) {
            fetchPartnersData();
            // Reset form when dialog opens
            setFormData({
                partnersId: 0,
                documentType: '',
            });
            setUploadedFiles([]);
            setIsDragOver(false);
        }
    }, [Open]);

    const handleFileSelect = (files: FileList | null) => {
        if (!files) return;
        
        const invalidFiles: string[] = [];
        
        // Check file types
        Array.from(files).forEach(file => {
            const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
            if (!ALLOWED_FILE_TYPES.includes(fileExtension as any)) {
                invalidFiles.push(file.name);
            }
        });
        
        // Show error if invalid files found
        if (invalidFiles.length > 0) {
            Swal.fire({
                title: 'Invalid File Type',
                text: `Only PDF, DOC, DOCX, XLS, and XLSX files are allowed. Invalid files: ${invalidFiles.join(', ')}`,
                icon: 'error',
                confirmButtonColor: '#02A4E3'
            });
            return;
        }
        
        const newFiles = Array.from(files).map(createFileObject);
        setUploadedFiles(prev => [...prev, ...newFiles]);

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const removeFile = (fileId: string) => {
        setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const uploadedFilesColumns: GridColDef[] = useMemo(() => [
        {
            field: 'fileName',
            headerName: 'File Name',
            flex: 1,
            minWidth: 200,
            renderCell: (params) => (
                <Typography sx={{ 
                    overflow: 'initial', 
                    whiteSpace: 'break-spaces',
                    lineHeight: 'normal',
                    color: '#162E3C',
                }}>
                    {params.value || '-'}
                </Typography>
            ),
        },
        {
            field: 'sizeText',
            headerName: 'File Size',
            flex: 0.6,
            minWidth: 120,
        },
        {
            field: 'actions',
            headerName: 'Actions',
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            flex: 0.5,
            minWidth: 120,
            renderCell: (params) => (
                <IconButton 
                    color="error" 
                    onClick={() => removeFile(params.row.id)}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 6H5M5 6H21M5 6V20C5 20.5304 5.21071 21.0391 5.58579 21.4142C5.96086 21.7893 6.46957 22 7 22H17C17.5304 22 18.0391 21.7893 18.4142 21.4142C18.7893 21.0391 19 20.5304 19 20V6H5ZM8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6" stroke="#162E3C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </IconButton>
            ),
        },
    ], [removeFile]);

    const dataGridRows = useMemo(() => 
        uploadedFiles.map((fileObj) => ({
            id: fileObj.id,
            fileName: fileObj.name,
            sizeText: formatFileSize(fileObj.size),
        })),
        [uploadedFiles]
    );

    const FMSelectPartnersCustom: React.FC<FieldAttributes<SelectProps>> = ({ variant, ...props }) => {
        const [field, meta, helpers] = useField<{}>(props);

        return (
            <FormControl fullWidth>
                <Select
                    {...field}
                    labelId="Client-Select-Label"
                    className="border-round custom-menu-item"
                    IconComponent={ExpandMoreIcon}
                    label={null}
                    displayEmpty
                    renderValue={(selected: unknown) => {
                        if (!selected || selected === 0) {
                            return <span style={{ color: '#999' }}>Select Client</span>;
                        }
                        const partner = partners.find(p => p.partnersId === selected);
                        return <span>{partner ? partner.customerName : ''}</span>;
                    }}
                >
                    <MenuItem value={0} onClick={() => helpers.setValue(0)}>
                        Select Client
                    </MenuItem>
                    {partners.filter(p => p.partnersId !== 0).map((partner, key) => (
                        <MenuItem key={key} value={partner.partnersId}>
                            {partner.customerName}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        );
    };

    const FMSelectTypeCustom: React.FC<FieldAttributes<SelectProps>> = ({ variant, ...props }) => {
        const [field, meta, helpers] = useField<{}>(props);

        return (
            <FormControl fullWidth>
                <Select
                    {...field}
                    labelId="Type-Select-Label"
                    className="border-round custom-menu-item"
                    IconComponent={ExpandMoreIcon}
                    label={null}
                    displayEmpty
                    renderValue={(selected: unknown) => {
                        if (!selected || selected === '') {
                            return <span style={{ color: '#999' }}>Select Type</span>;
                        }
                        return <span>{selected as string}</span>;
                    }}
                >
                    <MenuItem value="" onClick={() => helpers.setValue('')}>
                        Select Type
                    </MenuItem>
                    <MenuItem value="New York Stamped Declarations Page(s)">New York Stamped Declarations Page(s)</MenuItem>
                    <MenuItem value="Illinois Countersignature Page(s)">Illinois Countersignature Page(s)</MenuItem>
                </Select>
            </FormControl>
        );
    };


    return (
        <Dialog open={Open} onClose={handleClose} maxWidth='lg' fullWidth sx={{ width: '90%', margin: '0 auto' }}>
            <DialogTitle>
                <Typography 
                    variant="h6" 
                    className='card-title' 
                    sx={{ fontSize: '23px !important' }}
                    style={{ fontSize: '23px' }}
                >
                    I Want To... Send a Stamped Document
                </Typography>
            </DialogTitle>
            <Formik 
                initialValues={formData}
                enableReinitialize={true}
                validateOnChange={true}
                onSubmit={(data, { setSubmitting }) => {
                    // Validation
                    if (!data.partnersId || data.partnersId === 0) {
                        Swal.fire({
                            title: 'Validation Error',
                            text: 'Please select a client',
                            icon: 'warning',
                            confirmButtonText: 'OK',
                        });
                        setSubmitting(false);
                        return;
                    }
                    if (!data.documentType) {
                        Swal.fire({
                            title: 'Validation Error',
                            text: 'Please select a document type',
                            icon: 'warning',
                            confirmButtonText: 'OK',
                        });
                        setSubmitting(false);
                        return;
                    }
                    if (!uploadedFiles || uploadedFiles.length === 0) {
                        Swal.fire({
                            title: 'Validation Error',
                            text: 'Please select at least one file to upload',
                            icon: 'warning',
                            confirmButtonText: 'OK',
                        });
                        setSubmitting(false);
                        return;
                    }

                    const clientName = partners.find((partner) => partner.partnersId === data.partnersId)?.customerName || 'the selected client';
                    
                    Swal.fire({
                        title: '<div style="color: white;">Are you sure?</div>',
                        html: `<div style="color: white;">This will send a stamped document to the Client Portal for <span style="color: yellow;">${clientName}</span></div>`,
                        showCancelButton: true,
                        confirmButtonText: 'Yes, Proceed',
                        cancelButtonText: 'No, Continue to EDIT',
                        confirmButtonColor: '#4caf50',
                        cancelButtonColor: '#ff7b00',
                        reverseButtons: true,
                        background: '#0d4a63',
                    }).then(async (result) => {
                        if (result.isConfirmed) {
                            setSubmitting(true);
                            setLoading(true);
                            
                            try {
                                // Prepare FormData for API call
                                const formData = new FormData();
                                
                                // Append JSON data
                                formData.append('data', JSON.stringify({
                                    partnersId: data.partnersId,
                                    documentType: data.documentType,
                                }));
                                
                                // Append files
                                uploadedFiles.forEach((fileObj, index) => {
                                    formData.append(`file_${index}`, fileObj.file);
                                    formData.append(`fileName_${index}`, fileObj.name);
                                });
                                
                                formData.append('fileCount', uploadedFiles.length.toString());
                                
                                // Make API call
                                const response = await apiClient('/StampedDocument/sendToClient', {
                                    method: 'POST',
                                    headers: new Headers({
                                        // Don't set Content-Type header - browser will set it with boundary for FormData
                                        'Authorization': `Bearer ${token}`,
                                    }),
                                    body: formData,
                                });
                                
                                if (response?.status === true) {
                                    Swal.fire({
                                        title: 'Success',
                                        text: response.message || 'The stamped document has been sent to the client.',
                                        icon: 'success',
                                        confirmButtonText: 'OK',
                                        confirmButtonColor: '#02A4E3',
                                    });
                                    // Reset uploaded files
                                    setUploadedFiles([]);
                                    handleClose();
                                    if (onSuccess) {
                                        onSuccess();
                                    }
                                } else {
                                    Swal.fire({
                                        title: 'Error',
                                        text: response?.message || 'Failed to send stamped document to client.',
                                        icon: 'error',
                                        confirmButtonText: 'OK',
                                    });
                                }
                            } catch (error) {
                                console.error('Error sending stamped document to client:', error);
                                Swal.fire({
                                    title: 'Error',
                                    text: 'Failed to send stamped document to client. Please try again.',
                                    icon: 'error',
                                    confirmButtonText: 'OK',
                                });
                            } finally {
                                setSubmitting(false);
                                setLoading(false);
                            }
                        } else {
                            setSubmitting(false);
                        }
                    });
                }}
            >
                {({ values, isSubmitting, setFieldValue, errors }) => {
                    return (
                        <Form className='main-form'>
                            <DialogContent sx={{ width: '100%', padding: '16px' }}>
                                <Grid container spacing={2}>
                                    {/* Step 1: Select Client */}
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 600, fontSize: '14px' }}>
                                          Select Client
                                        </Typography>
                                        <Box sx={{ 
                                            borderRadius: '8px', 
                                            padding: '5px 7px',
                                            minHeight: '51px',
                                            minWidth: '50px',
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}>
                                            <FMSelectPartnersCustom name="partnersId" />
                                        </Box>
                                    </Grid>

                                    {/* Step 2: Select Type */}
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 600, fontSize: '14px' }}>
                                            Select Type
                                        </Typography>
                                        <Box sx={{ 
                                            borderRadius: '8px', 
                                            padding: '5px 7px',
                                            minHeight: '51px',
                                            minWidth: '50px',
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}>
                                            <Grid container spacing={2} alignItems="center">
                                                <Grid item xs={12} md={8}>
                                                    <FMSelectTypeCustom name="documentType" />
                                                </Grid>
                                                <Grid item xs={12} md={4}>
                                                    <Typography variant="body2" sx={{ color: '#666', fontSize: '12px' }}>
                                                        Either New York Stamped Declarations Page(s) or Illinois Countersignature Page(s)
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    </Grid>

                                    {/* Step 3: Select File or Drag and Drop */}
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 600, fontSize: '14px' }}>
                                            Select File(s) or Drag and Drop
                                        </Typography>
                                        <Paper
                                            sx={{
                                                border: isDragOver ? '2px dashed #02A4E3' : '1px dashed #ccc',
                                                borderRadius: 2,
                                                padding: 4,
                                                textAlign: 'center',
                                                backgroundColor: isDragOver ? '#f0f8ff' : '#fafafa',
                                                cursor: 'pointer',
                                                transition: 'all 0.3s ease',
                                                mb: 3,
                                                minHeight: 200,
                                                width: '100%',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'center',
                                                alignItems: 'center'
                                            }}
                                            onDragOver={(event) => {
                                                event.preventDefault();
                                                setIsDragOver(true);
                                            }}
                                            onDragLeave={(event) => {
                                                event.preventDefault();
                                                setIsDragOver(false);
                                            }}
                                            onDrop={(event) => {
                                                event.preventDefault();
                                                setIsDragOver(false);
                                                handleFileSelect(event.dataTransfer.files);
                                            }}
                                            onClick={() => fileInputRef.current?.click()}
                                        >
                                            <CloudUpload sx={{ fontSize: 48, color: '#02A4E3', mb: 2 }} />
                                            <Typography variant="h6" sx={{ color: '#666', mb: 1 }}>
                                                Select File or Drag and Drop
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#999' }}>
                                                Click to browse files or drag and drop multiple files here
                                            </Typography>
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                multiple
                                                accept={FILE_ACCEPT_ATTRIBUTE}
                                                style={{ display: 'none' }}
                                                onChange={(e) => handleFileSelect(e.target.files)}
                                            />
                                        </Paper>

                                        {/* Uploaded Files DataGrid */}
                                        {uploadedFiles.length > 0 && (
                                            <Box sx={{ mb: 3 }}>
                                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#333' }}>
                                                    Attached Documents ({uploadedFiles.length})
                                                </Typography>
                                                <Box sx={{ width: '100%', mb: 2 }}>
                                                    <DataGridPro
                                                        autoHeight
                                                        rows={dataGridRows}
                                                        columns={uploadedFilesColumns}
                                                        disableSelectionOnClick
                                                        disableColumnMenu
                                                        pageSize={pageSize}
                                                        pagination
                                                        rowCount={uploadedFiles.length}
                                                        paginationMode="client"
                                                        onPageChange={setCurrentPage}
                                                        onPageSizeChange={(newPageSize) => {
                                                            setPageSize(newPageSize);
                                                            setCurrentPage(0);
                                                        }}
                                                        rowsPerPageOptions={getRowsPerPageOptions(uploadedFiles.length, pageSize)}
                                                        sx={{
                                                            borderRadius: '10px',
                                                            overflow: 'hidden',
                                                            fontFamily: "'Proxima Nova', sans-serif",
                                                            fontSize: '16px',
                                                            fontWeight: 'normal',
                                                            '& .MuiDataGrid-main': {
                                                                overflow: 'hidden'
                                                            },
                                                            '& .MuiDataGrid-columnHeaderTitle': {
                                                                whiteSpace: 'normal',
                                                                lineHeight: 'normal',
                                                            },
                                                            border: '0px'
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                        )}
                                    </Grid>
                                </Grid>
                            </DialogContent>
                            <DialogActions sx={{ padding: '12px 16px', justifyContent: 'space-between' }}>
                                <button 
                                    className="btn-2 btn d-flex btn-primary search-btn" 
                                    onClick={handleClose} 
                                    type='button'
                                >
                                    Cancel
                                </button>
                                <button 
                                    className="btn btn-primary active-btn-blue search-btn" 
                                    type="submit" 
                                    disabled={isSubmitting}
                                    style={{ backgroundColor: '#4caf50', borderColor: '#4caf50' }}
                                >
                                    Send to Client
                                </button>
                            </DialogActions>
                        </Form>
                    );
                }}
            </Formik>
        </Dialog>
    );
};

export default AddStampedDocument;

